// src/components/MainLayout.js
import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Modal, Dropdown, Avatar } from "antd";
import {
  MenuOutlined,
  SearchOutlined,
  UserOutlined,
  LogoutOutlined,
  RightOutlined,
  BellOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  MdDashboard,
  MdManageAccounts,
  MdBrandingWatermark,
  MdAssignmentTurnedIn,
} from "react-icons/md";
import {
  FaOpencart,
  FaBook,
  FaLock,
  FaShippingFast,
  FaStore,
} from "react-icons/fa";
import { BsFillAwardFill, BsImage } from "react-icons/bs";
import { TbReport, TbReportAnalytics, TbMapPinCode } from "react-icons/tb";
import { ImEarth } from "react-icons/im";
import { BiSolidOffer } from "react-icons/bi";
import { LuContact } from "react-icons/lu";
import { HiMiniReceiptRefund } from "react-icons/hi2";
import { IoInformationCircleOutline } from "react-icons/io5";
import { useUser } from "../Context/UserContext";
import logo from "../Assets/Images/shaka-removebg-preview.png";
import "../Css/mainlayout.css";

/* ─────────────  MENU CONFIG  ───────────── */
const menuConfig = [
  {
    key: "admin",
    icon: <MdDashboard size={19} />,
    label: "Dashboard",
    path: "/admin",
  },
  {
    key: "masters",
    icon: <TbReportAnalytics size={19} />,
    label: "Masters",
    sectionLabel: "MASTER MANAGEMENT",
    children: [
      { key: "admin/product-list", icon: <ImEarth size={16} />, label: "Product Master" },
      { key: "admin/add-category", icon: <FaOpencart size={16} />, label: "Category Master" },
      { key: "admin/sub-list", icon: <FaOpencart size={16} />, label: "Subcategory Master" },
      // { key: "admin/user", icon: <MdManageAccounts size={16} />, label: "User Master" },
      { key: "admin/customer", icon: <MdManageAccounts size={16} />, label: "Customer Master" },
      { key: "admin/vendor", icon: <MdManageAccounts size={16} />, label: "Vendor Master" },
      { key: "admin/pincode", icon: <TbMapPinCode size={16} />, label: "Pincode Master" },
      { key: "admin/brand", icon: <MdBrandingWatermark size={16} />, label: "Brand Master" },
      // { key: "admin/store", icon: <FaStore size={16} />, label: "Store Master" },
      { key: "admin/banner", icon: <BsImage size={16} />, label: "Banner Master" },
      { key: "admin/offers", icon: <BiSolidOffer size={16} />, label: "Offers Master" },
    ],
  },
  {
    key: "admin/promo",
    icon: <BsFillAwardFill size={19} />,
    label: "Promo Codes",
    sectionLabel: "PROMOTIONS",
  },
  {
    key: "admin/deliveryBoy",
    icon: <TbReport size={19} />,
    label: "Delivery Boy",
    sectionLabel: "DELIVERY",
  },
  {
    key: "admin/assignOrders",
    icon: <MdAssignmentTurnedIn size={19} />,
    label: "Assign Orders",
  },
  {
    key: "legal",
    icon: <IoInformationCircleOutline size={19} />,
    label: "Legal Info",
    sectionLabel: "LEGAL & POLICIES",
    children: [
      { key: "admin/terms", icon: <FaBook size={15} />, label: "Terms & Conditions" },
      { key: "admin/privacyPolicy", icon: <FaLock size={15} />, label: "Privacy Policy" },
      { key: "admin/shippingPolicy", icon: <FaShippingFast size={15} />, label: "Shipping Policy" },
      { key: "admin/refundPolicy", icon: <HiMiniReceiptRefund size={15} />, label: "Refund Policy" },
      { key: "admin/contact", icon: <LuContact size={15} />, label: "Contact" },
    ],
  }
];

/* ─────────────  SIDEBAR ITEM  ───────────── */
const SidebarItem = ({ item, collapsed, currentPath, onNavigate }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isChildActive = hasChildren && item.children.some(
    (c) => currentPath === `/${c.key}`
  );
  const [open, setOpen] = useState(isChildActive);
  const isActive = currentPath === `/${item.key}`;

  // Keep submenu open when child is active
  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [isChildActive]);

  const handleClick = () => {
    if (hasChildren) {
      setOpen((prev) => !prev);
    } else {
      onNavigate(item.key);
    }
  };

  return (
    <>
      <div
        className={`sidebar-item${isActive || isChildActive ? " active" : ""}`}
        onClick={handleClick}
        title={collapsed ? item.label : undefined}
      >
        <span className="item-icon">{item.icon}</span>
        <span className="item-label">{item.label}</span>
        {hasChildren && (
          <span className={`item-arrow${open ? " open" : ""}`}>
            <RightOutlined />
          </span>
        )}
      </div>

      {hasChildren && (
        <div className={`sidebar-submenu${open ? " open" : ""}`}>
          {item.children.map((child) => (
            <div
              key={child.key}
              className={`sidebar-sub-item${
                currentPath === `/${child.key}` ? " active" : ""
              }`}
              onClick={() => onNavigate(child.key)}
            >
              <span className="item-icon" style={{ fontSize: 14 }}>
                {child.icon}
              </span>
              <span className="item-label">{child.label}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

/* ─────────────  MAIN LAYOUT  ───────────── */
const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useUser();
  const sidebarRef = useRef(null);

  const userName = localStorage.getItem("UserName") || "Admin User";
  const userEmail = localStorage.getItem("UserEmail") || "";
  const userRole = localStorage.getItem("Role") || "Administrator";
  const currentPath = location.pathname;

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Handle outside click on mobile
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        mobileOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [mobileOpen]);

  const handleSignout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("Role");
    localStorage.removeItem("UserName");
    setUser(null);
    navigate("/", { replace: true });
  };

  const showLogoutConfirm = () => {
    Modal.confirm({
      title: "Confirm Logout",
      content: "Are you sure you want to logout?",
      okText: "Yes, Logout",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: handleSignout,
    });
  };

  const onNavigate = (key) => {
    navigate(`/${key}`);
  };

  /* ── determine sidebar classes ── */
  const isMobile = () => window.innerWidth <= 768;
  const isTablet = () => window.innerWidth > 768 && window.innerWidth <= 1024;

  const getSidebarClass = () => {
    let cls = "admin-sidebar";
    if (isMobile()) {
      if (mobileOpen) cls += " mobile-open";
    } else if (isTablet()) {
      if (!collapsed) cls += " collapsed";
      if (mobileOpen) cls += " mobile-open";
    } else {
      if (collapsed) cls += " collapsed";
    }
    return cls;
  };

  const getMainClass = () => {
    let cls = "admin-main";
    if (isMobile()) return cls;
    if (isTablet()) {
      if (mobileOpen) cls += " sidebar-mobile-open";
      return cls;
    }
    if (collapsed) cls += " sidebar-collapsed";
    return cls;
  };

  /* User dropdown items */
  const userMenuItems = [
    {
      key: "profile",
      label: (
        <div style={{ padding: "4px 0" }}>
          <div style={{ fontWeight: 600 }}>{userName}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{userEmail || userRole}</div>
        </div>
      ),
      icon: <Avatar size={32} icon={<UserOutlined />} src="https://api.dicebear.com/7.x/avataaars/svg?seed=Admin" />,
    },
    { type: "divider" },
    {
      key: "logout",
      label: "Logout",
      icon: <LogoutOutlined />,
      danger: true,
      onClick: showLogoutConfirm,
    },
  ];

  return (
    <div className="admin-root">
      {/* ──────────── TOP HEADER ──────────── */}
      <header className="admin-header">
        {/* Left: Hamburger + Brand */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <button
            className="header-hamburger"
            onClick={() => {
              if (isMobile() || isTablet()) {
                setMobileOpen((p) => !p);
              } else {
                setCollapsed((p) => !p);
              }
            }}
            aria-label="Toggle sidebar"
          >
            <MenuOutlined />
          </button>

          <div className="header-brand" onClick={() => navigate("/admin")} style={{ cursor: "pointer" }}>
            <img src={logo} alt="Shakambari Logo" className="header-logo" />
            <div>
              <div className="header-brand-name">SHAKAMBARI</div>
              <div className="header-brand-sub">Admin Panel</div>
            </div>
          </div>
        </div>

        {/* Center: Search */}
        <div className="header-search">
          <div className="header-search-inner">
            <input
              className="header-search-input"
              placeholder="Search products, orders, customers..."
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
            />
            <button className="header-search-btn" aria-label="Search">
              <SearchOutlined />
            </button>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="header-actions">
          <button className="header-action-btn" aria-label="Notifications">
            <BellOutlined style={{ fontSize: 16 }} />
            <span className="btn-label">Alerts</span>
          </button>

          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            arrow
            trigger={["click"]}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
            >
              <Avatar
                size={34}
                icon={<UserOutlined />}
                src="https://api.dicebear.com/7.x/avataaars/svg?seed=Admin"
                style={{ border: "2px solid rgba(255,255,255,0.6)", cursor: "pointer" }}
              />
              <div className="header-user-info">
                <span className="header-user-name">
                  {userName || "Admin User"}
                </span>
                <span className="header-user-email">
                  {userEmail || userRole}
                </span>
              </div>
            </div>
          </Dropdown>
        </div>
      </header>

      {/* ──────────── BODY ──────────── */}
      <div className="admin-body">
        {/* Overlay for mobile */}
        <div
          className={`sidebar-overlay${mobileOpen ? " visible" : ""}`}
          onClick={() => setMobileOpen(false)}
        />

        {/* ──────────── SIDEBAR ──────────── */}
        <aside className={getSidebarClass()} ref={sidebarRef}>
          <nav className="sidebar-nav">
            {(() => {
              let lastSection = null;
              return menuConfig.map((item) => {
                const sectionHeader =
                  item.sectionLabel && item.sectionLabel !== lastSection ? (
                    <div className="sidebar-section-label" key={`section-${item.key}`}>
                      {item.sectionLabel}
                    </div>
                  ) : null;

                if (item.sectionLabel) lastSection = item.sectionLabel;

                return (
                  <React.Fragment key={item.key}>
                    {sectionHeader}
                    <SidebarItem
                      item={item}
                      collapsed={collapsed && !mobileOpen}
                      currentPath={currentPath}
                      onNavigate={onNavigate}
                    />
                  </React.Fragment>
                );
              });
            })()}
          </nav>
        </aside>

        {/* ──────────── MAIN CONTENT ──────────── */}
        <main className={getMainClass()}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
